import type { ActivityState } from '@/features/presence/types';
import { isWithinQuietHours } from './quietHours';
import type {
  NotificationItem, NotificationRule, NotificationSettings, SuppressReason,
} from './types';

export interface DeliveryDecision {
  /** Show a banner/toast + play a sound. */
  banner: boolean;
  /** Add to the notification centre regardless of banner suppression. */
  store: boolean;
  sound: boolean;
  desktop: boolean;
  reason?: SuppressReason;
}

/**
 * Kinds addressed to a PERSON, which a mute that still allows mentions lets
 * through. A task notice is one: it goes to the people named on the task, not
 * to a room (the server's `DIRECT_KINDS` says the same).
 */
const MENTION_KINDS = new Set([
  'mention', 'reply', 'callIncoming', 'approvalRequest',
  'taskAssigned', 'taskUpdated', 'taskComment', 'taskDue',
]);

function matchRule(rule: NotificationRule, item: NotificationItem): boolean {
  if (!rule.enabled) return false;
  const { keywords, senderIds, scope, kinds } = rule.match;
  if (kinds && !kinds.includes(item.kind)) return false;
  if (senderIds && (!item.actorId || !senderIds.includes(item.actorId))) return false;
  if (scope && (item.scopeKind !== scope.kind || item.scopeId !== scope.id)) return false;
  if (keywords && keywords.length > 0) {
    const haystack = `${item.title} ${item.body ?? ''} ${item.preview.snippet ?? ''}`.toLowerCase();
    if (!keywords.some((word) => haystack.includes(word.toLowerCase()))) return false;
  }
  return true;
}

function isMuted(settings: NotificationSettings, item: NotificationItem, now: number): boolean {
  const mute = settings.mutes.find(
    (entry) => entry.scopeKind === item.scopeKind && entry.scopeId === item.scopeId,
  );
  if (!mute) return false;
  if (mute.until !== null && mute.until <= now) return false;
  return !(mute.allowMentions && MENTION_KINDS.has(item.kind));
}

const suppressed = (reason: SuppressReason): DeliveryDecision => ({
  banner: false, store: true, sound: false, desktop: false, reason,
});

/**
 * Single source of truth for "should this interrupt the user?".
 * Order: custom rules > mute ledger > DND presence > quiet hours > per-kind level.
 * A suppressed notification is still stored — the centre shows everything.
 */
/**
 * The two presence-automation toggles that decide whether being busy silences
 * banners. Mirrors `cyphex.user_presence.dnd_during_calls / _meetings`, which
 * default to true.
 */
export interface DndAutomation {
  dndDuringCalls: boolean;
  dndDuringMeetings: boolean;
}

const DEFAULT_AUTOMATION: DndAutomation = { dndDuringCalls: true, dndDuringMeetings: true };

export function decideDelivery(
  item: NotificationItem,
  settings: NotificationSettings,
  presence: ActivityState,
  now: number = Date.now(),
  automation: DndAutomation = DEFAULT_AUTOMATION,
): DeliveryDecision {
  const rule = [...settings.rules]
    .sort((a, b) => a.priority - b.priority)
    .find((candidate) => matchRule(candidate, item));

  if (rule?.action.suppress) return suppressed('rule');

  const urgent = item.priority === 'urgent' || rule?.action.priority === 'urgent';
  const isCall = item.kind === 'callIncoming';

  if (isMuted(settings, item, now)) return suppressed('muted');

  const dnd = presence === 'doNotDisturb' || presence === 'presenting' || presence === 'screenSharing';
  /* QUIET WHILE I AM ON A CALL — the mirror of services/notifyDelivery.ts.
     A FALLBACK ONLY: the server ships a verdict with every push and ingest.ts
     obeys that instead, which matters here because on desktop a call runs in
     its OWN WINDOW and this document's presence never says `inACall` at all.
     Checked independently of `respectDnd`, exactly as on the server: "I am in a
     call" is a different fact from "I set myself to Do not disturb". */
  const busyInCall =
    (automation.dndDuringCalls && presence === 'inACall') ||
    (automation.dndDuringMeetings && presence === 'inAMeeting');
  if ((settings.respectDnd && dnd) || busyInCall) {
    /* An incoming call still pierces: it is written `urgent`, and a phone you
       cannot hear is worse than a banner you did not want. */
    if (!urgent) return suppressed('dnd');
  }

  if (isWithinQuietHours(settings.quietHours, new Date(now))) {
    const pierces =
      (urgent && settings.quietHours.allowUrgent) || (isCall && settings.quietHours.allowCalls);
    if (!pierces) return suppressed('quiet_hours');
  }

  const level = rule?.action.level ?? settings.perKind[item.kind] ?? 'all';
  if (level === 'none') return suppressed('level');
  if (level === 'mentions' && !MENTION_KINDS.has(item.kind)) return suppressed('level');

  const sound = settings.soundEnabled && rule?.action.sound !== false;
  return {
    banner: settings.inappEnabled,
    store: true,
    sound,
    desktop: settings.desktopBanner && settings.pushEnabled,
  };
}
